<template>
  <div
    :class="[
      {
        'form-container': isDataLoaded,
        'form-container--border-hidden': !isDataLoaded,
      },
    ]"
  >
    <form
      class="form-block"
      v-if="(!isDataLoaded || isTrackingInfoEmpty) && !isReplacingViewActive"
      @submit.prevent="checkDeliveryStatus"
    >
      <div class="input-zone">
        <div class="shipmentContainer">
          <label for="parcelNumber">{{ $t('tracking.form.parcelNumber') }}</label>
          <input
            @focusin="showBarShipment = true"
            @focusout="showBarShipment = false"
            @input="shipment.trim().length > 8 ? (postcode = '') : null"
            id="parcelNumber"
            name="parcelNumber"
            inputmode="numeric"
            autocomplete="off"
            spellcheck="false"
            type="text"
            :class="shipmentClasses"
            aria-describedby="parcelNumber-error"
            :placeholder="$t('tracking.form.parcelNumberPlaceholder')"
            v-model="shipment"
          />
          <span class="info-container">
            <img
              src="/Scripts/vue/tracking/img/ico_info.svg"
              :alt="$t('tracking.alt.info_picto')"
            />
            {{ $t('tracking.form.parcelNumberInfo') }}
          </span>
          <div
            aria-live="assertive"
            role="alert"
            style="display: none; color: red"
            id="parcelNumber-error"
          >
            {{ $t("tracking.form.messageError.default.parcelNumber") }}
          </div>
        </div>

        <template v-if="!fullShipment">
          <div class="zipcodeContainer">
            <label for="zipCode">{{ $t('tracking.form.zipcode') }}</label>
            <input
              @focusin="showBarZipcode = true"
              @focusout="showBarZipcode = false"
              type="text"
              id="zipCode"
              name="zipCode"
              inputmode="numeric"
              autocomplete="off"
              spellcheck="false"
              :class="zipClasses"
              :placeholder="$t('tracking.form.zipcodePlaceholder')"
              v-model="postcode"
              style="margin-top: 10px"
              aria-describedby="zipCode-error"
            />
            <div
              aria-live="assertive"
              role="alert"
              style="display: none; color: red"
              id="zipCode-error"
            >
              {{ $t("tracking.form.messageError.default.zipcode") }}
            </div>
          </div>
        </template>

        <button :disabled="!isShipmentValid" type="submit" class="btn btn-primary search-btn">
          {{ $t('tracking.form.searchBtn') }}
        </button>
      </div>

      <div class="errorsMessages" v-if="errors.length > 0">
        <p v-for="(error, index) in errors" :key="index">{{ error }}</p>
      </div>
    </form>

    <div v-if="isDataLoaded && !isTrackingInfoEmpty && trackingInfo.trackingNumber">
      <TrackingInfoHeader
        :tracking-number="trackingInfo.trackingNumber"
        :status="trackingInfo.status"
        :shipper="trackingInfo.shipper"
        :nature-points-relais="trackingInfo.naturePointsRelais"
        :update-date="trackingInfo.updateDate"
        :update-time="trackingInfo.updateTime"
      />
      <PickupLocationInfo
        :location-name="pickupLocationInfo?.locationName || ''"
        :closing-time="pickupLocationInfo?.closingTime || ''"
        :address="pickupLocationInfo?.address || ''"
        :pickup-location-info="pickupLocationInfo"
        :status="trackingInfo.status"
      />
      <FiveStepsTracking />
    </div>

    <div v-if="multipleChoices" class="choices-list">
      <div class="col-md-12 title">{{ $t('tracking.form.multipleResults.title') }} :</div>
      <div
        class="col-md-3"
        v-for="choice in choices"
        :key="choice?.Enseigne?.CodeMarque || choice?.Enseigne?.Libelle"
      >
        <a class="btn btn-primary" @click="checkDeliveryStatusChoice(choice.Enseigne.CodeMarque)">
          {{ choice.Enseigne.Libelle }}
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import TrackingInfoHeader from '@/components/TrackingInfoHeader/TrackingInfoHeader';
import PickupLocationInfo from '@/components/PickupLocationInfo/PickupLocationInfo';
import FiveStepsTracking from '../FiveStepsTracking/FiveStepsTracking.vue';
import { mapState } from 'vuex';
import { isAmazonLocker } from '../../utils/amazonLocker';

export default {
  components: {
    TrackingInfoHeader,
    PickupLocationInfo,
    FiveStepsTracking,
  },
  data() {
    return {
      shipment: '',
      postcode: '',
      brand: '',
      errors: [],
      showBarShipment: false,
      showBarZipcode: false,
      isDataLoaded: false,
      trackingInfo: {},
      pickupLocationInfo: null,
    };
  },
  props: {
    externalBrand: String,
    externalShipment: String,
    externalPostcode: String,
  },
  computed: {
    ...mapState([
      'multipleChoices',
      'choices',
      'isReplacingViewActive',
      'minLengthNumExpe',
      'maxLengthNumExpe',
      'nameLangue',
      'doesParameterSet',
      'expeditionStatus',
      'contextualTracking',
      'params',
    ]),
    locale() {
      const l = this.nameLangue || 'fr-FR';
      return l.length === 4 && !l.includes('-') ? `${l.slice(0, 2)}-${l.slice(2)}` : l;
    },
    shipmentTrimmed() {
      return this.shipment.trim();
    },
    isShipmentValid() {
      const len = this.shipmentTrimmed.length;
      return len >= this.minLengthNumExpe && len <= this.maxLengthNumExpe;
    },
    fullShipment() {
      return this.shipmentTrimmed.length > 8;
    },
    shipmentClasses() {
      const len = this.shipmentTrimmed.length;
      const inRange = (len >= this.minLengthNumExpe && len <= this.maxLengthNumExpe) || len === 0;
      return [
        'parcelnumber',
        this.fullShipment ? 'single-input' : '',
        inRange ? '' : 'error',
        this.showBarShipment && inRange ? 'input-underline' : '',
      ];
    },
    zipClasses() {
      return ['zipcode', this.showBarZipcode ? 'input-underline' : ''];
    },
    validateShipmentFormat() {
      const minLength = this.minLengthNumExpe;
      const maxLength = this.maxLengthNumExpe;
      const shipmentTrimmed = this.shipment.trim();

      if (!shipmentTrimmed) {
        return {
          isValid: false,
          message: this.$t('tracking.form.messageError.emptyParcelNumber'),
        };
      }

      if (shipmentTrimmed.length < minLength || shipmentTrimmed.length > maxLength) {
        return {
          isValid: false,
          message: this.$t('tracking.form.messageError.invalidLength'),
        };
      }

      if (!/^\d+$/.test(shipmentTrimmed)) {
        return {
          isValid: false,
          message: this.$t('tracking.form.messageError.invalidFormat'),
        };
      }

      return { isValid: true };
    },
    isTrackingInfoEmpty() {
      if (!this.trackingInfo) return true;
      return Object.values(this.trackingInfo).every(val => {
        if (Array.isArray(val)) return val.length === 0;
        if (val == null) return true;
        if (typeof val === 'string') return val.trim() === '';
        return false;
      });
    },
  },
  methods: {
    checkDeliveryStatus() {
      const formattedLocale = this.locale;

      this.errors = [];
      this.isDataLoaded = false;

      this.$store.commit('setFiveStepsTracking', {});
      this.$store.commit('setDeliveryCheckpoints', {});

      if (!this.isShipmentValid) {
        this.$t('tracking.form.messageError.noResult').forEach(m => {
          this.errors.push(m);
        });
        return;
      }

      this.$store
        .dispatch('checkDeliveryStatus', {
          shipment: this.shipment.trim(),
          postcode: this.postcode.trim(),
          brand: this.brand,
          codePays: formattedLocale,
        })
        .then(response => {
          const naturesPointRelais = response.Expedition.NaturesPointRelaisCompatibles || [];
          const newNaturesPointRelais = response.Expedition.NaturesPointRelais;

          const lastEventArr = response.Expedition?.Evenements || [];
          const lastElement = lastEventArr[lastEventArr.length - 1] || {};
          const fullDateTime = lastElement.Date || '';

          const dateObj = new Date(fullDateTime);

          const updateDate = Number.isNaN(dateObj.getTime())
            ? ''
            : dateObj.toLocaleDateString(this.locale);
          const updateTime = Number.isNaN(dateObj.getTime())
            ? ''
            : dateObj.toLocaleTimeString(this.locale, { hour: '2-digit', minute: '2-digit' });

          this.trackingInfo = {
            trackingNumber: this.expeditionStatus.expeditionNumber,
            status: this.contextualTracking,
            shipper: this.expeditionStatus.brand,
            naturePointsRelais: newNaturesPointRelais,
            updateDate: updateDate,
            updateTime: updateTime,
          };

          const evts = Array.isArray(response.Expedition?.Evenements)
            ? response.Expedition.Evenements
            : [];
          const lastDetailEvent = evts
            .slice()
            .reverse()
            .find(e => e?.DetailPointRelais);

          const suiviParEtapes = response.Expedition?.SuiviParEtapes || {};

          const stepWithoutEvent = Object.entries(suiviParEtapes).find(
            ([, step]) => !step?.Evenement
          );

          let stepNumber = 0;
          if (stepWithoutEvent) {
            stepNumber = parseInt(stepWithoutEvent[0], 10);
          } else {
            stepNumber = 5;
          }

          // Le point relais de livraison est prioritaire : le dernier relais des évènements peut être le relais de collecte
          const pickupDetail =
            response.Expedition?.PointRelaisLivraison || lastDetailEvent?.DetailPointRelais;

          if (pickupDetail && stepNumber > 4) {
            const detail = pickupDetail;

            const jsDayIndex = new Date().getDay(); // 0 = dimanche so need to convert
            const dayIndex = jsDayIndex === 0 ? 6 : jsDayIndex - 1;

            this.pickupLocationInfo = {
              locationName: detail.Adresse.Libelle,
              closingTime: this.formatClosingTime(
                detail.Horaires?.[dayIndex]?.HeureFermetureAM || "N/A"
              ),
              address: `${detail.Adresse.AdresseLigne1}, ${detail.Adresse.CodePostal} ${detail.Adresse.Ville}`,
              horaires: detail.Horaires,
              services: detail.Services,
              contact: {
                phone: detail.Telephone || this.$t('tracking.messageError.default.phone'),
                email: detail.Email || this.$t('tracking.messageError.default.email'),
              },
              geolocation: {
                latitude:
                  detail.Geolocalisation?.Latitude ||
                  detail.Adresse.Latitude ||
                  this.$t('tracking.messageError.default.latitude'),
                longitude:
                  detail.Geolocalisation?.Longitude ||
                  detail.Adresse.Longitude ||
                  this.$t('tracking.messageError.default.longitude'),
              },
              additionalInfo:
                detail.InfosComplementaires ||
                this.$t('tracking.messageError.default.additionalInfo'),
              id: detail.Numero,
              countryCode: detail.Adresse.CodePays,
              pickupEntity: isAmazonLocker(detail)
                ? 'Amazon Locker Partenaire'
                : detail.CodeNature?.includes('C')
                  ? 'Locker'
                  : 'Point Relais',
              isAmazonLocker: isAmazonLocker(detail),
            };
          }
          this.isDataLoaded = true;

          const naturesAllowed = (naturesPointRelais || []).join(',');
          this.$store.commit('setParcelshopNatureAllowed', naturesAllowed);

          if (response.status[0] && response.status[0].state === 'warn') {
            this.$t('tracking.form.messageError.noResult').forEach(m => {
              this.errors.push(m);
            });
          }
        })
        .catch(error => {
          const code = error?.response?.status;
          const key =
            code === 404
              ? 'tracking.form.messageError.apiDown'
              : 'tracking.form.messageError.noResult';
          this.$t(key).forEach(m => this.errors.push(m));
          this.$store.commit('setLoading', false);
        });
    },
    formatClosingTime(time) {
      return time.slice(0, 5);
    },
    checkDeliveryStatusChoice(clickedBrand) {
      this.errors = [];

      this.$store.commit('setFiveStepsTracking', {});
      this.$store.commit('setDeliveryCheckpoints', {});

      if (!this.isShipmentValid) {
        this.$t('tracking.form.messageError.noResult').forEach(m => {
          this.errors.push(m);
        });
        return;
      }

      const formattedLocale = this.locale;

      this.$store
        .dispatch('checkDeliveryStatus', {
          shipment: this.shipment.trim(),
          postcode: this.postcode.trim(),
          brand: clickedBrand,
          codePays: formattedLocale,
        })
        .then(response => {
          const naturesPointRelaisCompatibles =
            response.Expedition?.NaturesPointRelaisCompatibles || [];
          this.$store.commit('setParcelshopNatureAllowed', naturesPointRelaisCompatibles.join(','));

          if (response.status?.[0]?.state === 'warn') {
            this.$t('tracking.form.messageError.noResult').forEach(m => {
              this.errors.push(m);
            });
          }
        })
        .catch(error => {
          const code = error?.response?.status;
          const key =
            code === 404
              ? 'tracking.form.messageError.apiDown'
              : 'tracking.form.messageError.noResult';
          this.$t(key).forEach(m => this.errors.push(m));
          this.$store.commit('setLoading', false);
        });
    },
    resetForm() {
      this.shipment = '';
      this.postcode = '';
      this.brand = '';
    },
    scrollToAnchor(id = 'suivie_mon_colis') {
      this.$nextTick(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          console.warn(`[tracking] �l�ment #${id} introuvable pour le scroll.`);
        }
      });
    },
  },
  mounted() {
    if (this.externalBrand) this.brand = this.externalBrand;
    if (this.externalPostcode) this.postcode = this.externalPostcode;
    if (this.externalShipment) {
      this.shipment = this.externalShipment;
      this.checkDeliveryStatus();
    }
  },
  watch: {
    expeditionStatus: {
      deep: true,
      immediate: true,
      handler(newStatus) {
        if (newStatus && newStatus.expeditionNumber) {
          this.trackingInfo = {
            trackingNumber: newStatus.expeditionNumber,
            status: this.contextualTracking,
            shipper: newStatus.brand,
            naturePointsRelais: this.params.parcelshopNatureAllowed,
            updateDate: new Date().toLocaleDateString(this.locale),
            updateTime: new Date().toLocaleTimeString(this.locale, {
              hour: '2-digit',
              minute: '2-digit',
            }),
          };
          this.isDataLoaded = true;

          if (this.doesParameterSet) {
            this.scrollToAnchor('suivie_mon_colis');
          }
        }
      },
    },
  },
};
</script>

<style scoped lang="scss">
@import './Form.scss';
</style>
